// Supported communication languages
export enum CommunicationLanguage {
  French = 'fr',
  Dutch = 'nl', 
  English = 'en'
}

// Available delivery methods for contact communication
export enum DeliveryMethod {
  Email = 'email',
  InApp = 'in_app',
  SMS = 'sms',
  WhatsApp = 'whatsapp'
}

// Communication method configuration for a contact
export interface ContactCommunicationMethod {
  method: DeliveryMethod;
  enabled: boolean;
  verified?: boolean; // Whether the contact method has been verified
  value?: string; // The actual contact value (email address, phone number, etc.)
}

/**
 * Nature of a contact:
 *  - `personal`: a named human (first/last name) — the default for everyone.
 *  - `generic`: a functional mailbox of a professional tenant (e.g.
 *    `comptabilite@acme.com`) with no named person; its label is carried by
 *    `Contact.name` (e.g. "Comptabilité").
 */
export type ContactKind = 'personal' | 'generic';

export class Contact {
    firstName?: string;
    lastName?: string;
    name?: string;
    pro?: boolean;
    /** See {@link ContactKind}. Defaults to `personal`. */
    contactKind?: ContactKind;
    email?: string;
    phone?: string;
    comments?: string;
    preferredLanguage: CommunicationLanguage;
    
    // Communication method preferences
    communicationMethods: ContactCommunicationMethod[] = [];

    // Primary communication method (fallback)
    primaryCommunicationMethod?: DeliveryMethod;

    /**
     * Marks this contact as the tenant's primary point of contact. A tenant
     * may hold several contacts (e.g. co-tenants, a company's billing + site
     * contacts); exactly one should carry this flag. When none is flagged
     * (legacy data) callers fall back to the first contact in the array.
     */
    isPrimary?: boolean;

    /**
     * Read-only mirror of `users/{uid}.profile.whatsappOptIn` for the portal
     * account linked to this contact by e-mail, written by
     * `onTenantPortalProfileChanged` (#421). Never edited from this screen —
     * it is the TENANT's own consent on their portal, not the manager's
     * `communicationMethods` basis for messaging them. `undefined` means no
     * linked portal account has been observed yet, not "declined".
     */
    portalWhatsappOptIn?: boolean;

    /**
     * Read-only mirror of `users/{uid}.photoURL` for the same linked portal
     * account, written by `onTenantPortalProfileChanged`. The owner cannot
     * read the tenant's user doc; this is how their avatar reaches the
     * owner's screens. Absent = no photo, show initials.
     */
    portalPhotoURL?: string;

    constructor(data: Partial<Contact> = {}) {
        this.firstName = data.firstName;
        this.lastName = data.lastName;
        this.name = data.name;
        this.pro = data.pro || false;
        this.contactKind = data.contactKind || 'personal';
        this.email = data.email;
        this.phone = data.phone;
        this.comments = data.comments;
        this.portalWhatsappOptIn = data.portalWhatsappOptIn;
        this.portalPhotoURL = data.portalPhotoURL;
        this.preferredLanguage = data.preferredLanguage || CommunicationLanguage.French; // Default to French
        this.communicationMethods = data.communicationMethods || this.getDefaultCommunicationMethods();
        this.primaryCommunicationMethod = data.primaryCommunicationMethod || DeliveryMethod.Email;
        this.isPrimary = data.isPrimary ?? false;
    }
    
    /**
     * Get default communication methods based on available contact information
     */
    private getDefaultCommunicationMethods(): ContactCommunicationMethod[] {
        const methods: ContactCommunicationMethod[] = [];
        
        // Always enable email if available
        if (this.email) {
            methods.push({
                method: DeliveryMethod.Email,
                enabled: true,
                verified: false,
                value: this.email
            });
        }
        
        // Enable SMS if phone is available
        if (this.phone) {
            methods.push({
                method: DeliveryMethod.SMS,
                enabled: false, // Disabled by default, user must opt-in
                verified: false,
                value: this.phone
            });
        }
        
        // InApp is always available but disabled by default
        methods.push({
            method: DeliveryMethod.InApp,
            enabled: false,
            verified: true // In-app doesn't need verification
        });
        
        // WhatsApp disabled by default, requires phone number
        if (this.phone) {
            methods.push({
                method: DeliveryMethod.WhatsApp,
                enabled: false,
                verified: false,
                value: this.phone
            });
        }
        
        return methods;
    }
    
    /**
     * Get enabled communication methods for this contact
     */
    getEnabledCommunicationMethods(): ContactCommunicationMethod[] {
        return this.communicationMethods.filter(method => method.enabled);
    }
    
    /**
     * Check if a specific delivery method is available and enabled
     */
    hasDeliveryMethod(method: DeliveryMethod): boolean {
        return this.communicationMethods.some(cm => cm.method === method && cm.enabled);
    }
    
    /**
     * Update communication method configuration
     */
    updateCommunicationMethod(method: DeliveryMethod, config: Partial<ContactCommunicationMethod>): void {
        const existingIndex = this.communicationMethods.findIndex(cm => cm.method === method);
        
        if (existingIndex >= 0) {
            // Update existing method
            this.communicationMethods[existingIndex] = {
                ...this.communicationMethods[existingIndex],
                ...config,
                method // Ensure method doesn't change
            };
        } else {
            // Add new method
            this.communicationMethods.push({
                method,
                enabled: false,
                verified: false,
                ...config
            });
        }
    }
}